// Visual identity of each POI category on the concierge map: color + glyph
// (inline lucide paths, since map markers need raw HTML, not React).

export type PoiCategory =
  | "restaurant"
  | "attraction"
  | "beach"
  | "essential"
  | "event"
  | "place";

type CategoryMeta = {
  color: string;
  labelKey: string;
  glyph: string; // inner SVG markup, 24x24 viewBox, stroke-based
};

const GLYPHS = {
  utensils:
    '<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>',
  landmark:
    '<line x1="3" x2="21" y1="22" y2="22"/><line x1="6" x2="6" y1="18" y2="11"/><line x1="10" x2="10" y1="18" y2="11"/><line x1="14" x2="14" y1="18" y2="11"/><line x1="18" x2="18" y1="18" y2="11"/><polygon points="12 2 20 7 4 7"/>',
  umbrella:
    '<path d="M22 12a10.06 10.06 0 0 0-20 0Z"/><path d="M12 12v8a2 2 0 0 0 4 0"/><path d="M12 2v1"/>',
  cross:
    '<path d="M11 2a2 2 0 0 0-2 2v5H4a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h5v5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-5h5a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2h-5V4a2 2 0 0 0-2-2h-2z"/>',
  calendar:
    '<rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/>',
  bed:
    '<path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8"/><path d="M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"/><path d="M12 4v6"/><path d="M2 18h20"/>',
  pin:
    '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>',
};

export const HOTEL_GLYPH = GLYPHS.bed;

const CATEGORIES: Record<PoiCategory, CategoryMeta> = {
  restaurant: { color: "#f97316", labelKey: "cat_restaurant", glyph: GLYPHS.utensils },
  attraction: { color: "#8b5cf6", labelKey: "cat_attraction", glyph: GLYPHS.landmark },
  beach: { color: "#0ea5e9", labelKey: "cat_beach", glyph: GLYPHS.umbrella },
  essential: { color: "#10b981", labelKey: "cat_essential", glyph: GLYPHS.cross },
  event: { color: "#ec4899", labelKey: "cat_event", glyph: GLYPHS.calendar },
  place: { color: "#64748b", labelKey: "cat_place", glyph: GLYPHS.pin },
};

export const categoryMeta = (category?: string | null): CategoryMeta =>
  CATEGORIES[(category ?? "") as PoiCategory] ?? CATEGORIES.place;

// OSM raw values (subtype, cuisine) come snake_cased / lower — turn them into
// "Ice Cream", "Seafood" for display. Empty / nullish -> "".
export const humanize = (raw?: string | null): string =>
  (raw ?? "")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const glyphSvg = (inner: string, size = 15) =>
  `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
